import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LocalStorage } from './browser-storage/local-storage.service';
import { SessionStorage } from './browser-storage/session-storage.service';
import { TabData } from './tab.type';

@Injectable({
    providedIn: 'root'
})
export class TabService {
    private tabNamePrefix = 'suremgatab-';

    private stalenessInterval: number = 14400; // 4 hours between last tab liveness_timestamp and current time when tab will be removed from storage
    private inactivityInterval: number = 3600; // 1 hour
    private tabItems$: BehaviorSubject<TabData[]> = new BehaviorSubject([]);
    private tabObjRef = new Map();

    constructor(private localStorage: LocalStorage, private sessionStorage: SessionStorage) { }

    getTabItems(): Observable<TabData[]> {
        return this.tabItems$.asObservable();
    }

    handleTabChanges() {
        let tabs: TabData[] = this.getAllTabs();
        if (tabs.length > 0) {
            tabs.sort((a, b) => +a.Liveness_Timestamp - +b.Liveness_Timestamp);
        }

        this.tabItems$.next([...tabs]);
    }

    setTab(startRoute?: string, pageParams?: any, pageTitle?: string, username?: string, currentBook?: string): string {
        let tabGuid = this.generateUUID();

        let sig = this.tabNamePrefix + tabGuid;
        let tabName = this.tabNamePrefix + tabGuid;

        const tab: TabData = {
            Tab_Name: tabName,
            Current_Page_Title: pageTitle ? pageTitle : '',
            Liveness_Timestamp: this.getTimestamp(),
            Start_Route: startRoute ? startRoute : 'my-stats',
            Start_Route_Params: pageParams ? pageParams : {},
            Is_Active: true,
            Is_Reloading: false,
            User: username,
            Book: currentBook
        };
        this.localStorage.setItem(sig, JSON.stringify(tab));

        this.addTabItem(tab);

        return tabName;
    }

    getTab(tabName: string): TabData | null {
        let tabStrData = this.localStorage.getItem(tabName);
        if (tabStrData) {
            let tabData: TabData = JSON.parse(tabStrData);
            return { ...tabData };
        }
        return null;
    }

    getAllTabs(): TabData[] {
        let currentTabs: TabData[] = [];
        let tabNames = this.localStorage.getAllTabKeys();
        for (const tab of tabNames) {
            let tabDataStr = this.localStorage.getItem(tab);
            if (tabDataStr) {
                let tabData: TabData = JSON.parse(tabDataStr);
                currentTabs.push({ ...tabData });
            }
        }
        return currentTabs;
    }

    removeTab(tabName: string) {
        this.localStorage.removeItem(tabName);
        this.handleTabChanges();
    }

    addTabItem(tab: TabData) {
        let tabs: TabData[] = this.getAllTabs();
        tabs.sort((a, b) => +a.Liveness_Timestamp - +b.Liveness_Timestamp);
        this.tabItems$.next([...tabs]);
    }

    isLastTab(tabName: string) {
        const currentTabs = this.getAllTabs();
        if (currentTabs && currentTabs.length === 1) {
            if (currentTabs[0].Tab_Name === tabName) {
                return true;
            }
        }
        return false;
    }

    activeTabsExist() {
        const allTabs = this.getAllTabs();
        let atLeastOneActiveTab = allTabs.length > 0 && allTabs.some((tab) => !this.isTabStale(tab));
        const sessionTab = this.sessionStorage.getItem('sessiontab');
        return sessionTab !== null || atLeastOneActiveTab;
    }

    getActiveTabBookAndUser() {
        let activeTab: any = {};
        let activeTabBook;
        const sessionTabName = this.sessionStorage.getItem('sessiontab');
        if (sessionTabName) {
            let sessionTab: TabData = JSON.parse(sessionTabName);
            activeTabBook = sessionTab.Book;
            activeTab.Book = sessionTab.Book;
            activeTab.User = sessionTab.User;
        }

        if (!activeTabBook) {
            const firstNotStaleTab = this.getAllTabs().filter((tab) => !this.isTabStale(tab))[0];
            if (firstNotStaleTab) {
                activeTab.Book = firstNotStaleTab.Book;
                activeTab.User = firstNotStaleTab.User;
            }
        }

        return Object.keys(activeTab).length > 0 ? activeTab : null;
    }

    setTabPageTitle(title: string, tabName: string) {
        let tabToUpdate = this.getTab(tabName);
        if (tabToUpdate) {
            tabToUpdate.Current_Page_Title = title;
            tabToUpdate.Liveness_Timestamp = this.getTimestamp();
            const modifiedTab = JSON.stringify(tabToUpdate);
            this.localStorage.setItem(tabName, modifiedTab);
            this.handleTabChanges();
        }
    }

    updateTabStartRoute(tabName: string, newRoute: string, newParams?: any) {
        let tabToUpdate = this.getTab(tabName);
        if (tabToUpdate) {
            tabToUpdate.Start_Route = newRoute;
            tabToUpdate.Liveness_Timestamp = this.getTimestamp();
            if (newParams) {
                tabToUpdate.Start_Route_Params = newParams;
            } else {
                if (tabToUpdate.Start_Route_Params) {
                    tabToUpdate.Start_Route_Params = null;
                }
            }

            const modifiedTab = JSON.stringify(tabToUpdate);
            this.localStorage.setItem(tabName, modifiedTab);
            this.handleTabChanges();
        }
    }

    updateTabOnBookChange(tabName: string, newBook: string) {
        let tabToUpdate = this.getTab(tabName);
        if (tabToUpdate) {
            tabToUpdate.Book = newBook;
            tabToUpdate.Liveness_Timestamp = this.getTimestamp();
            const modifiedTab = JSON.stringify(tabToUpdate);
            this.localStorage.setItem(tabName, modifiedTab);
            this.handleTabChanges();
        }
    }

    onBrowserUnload(tabName: string) {
        if (tabName) {
            const currentTab = this.getTab(tabName);
            if (currentTab) {
                currentTab.Is_Reloading = true;
                currentTab.Liveness_Timestamp = this.getTimestamp();
                const sessionTab = JSON.stringify(currentTab);
                const sessionTabKey = `sessiontab`;
                this.sessionStorage.setItem(sessionTabKey, sessionTab);
                this.removeTab(tabName);
            }
        }
    }

    getSessionTab() {
        const sessionTabStr = this.sessionStorage.getItem('sessiontab');
        if (sessionTabStr) {
            let sessionTab: TabData = JSON.parse(sessionTabStr);
            sessionTab.Is_Reloading = false;
            sessionTab.Liveness_Timestamp = this.getTimestamp();
            this.localStorage.setItem(sessionTab.Tab_Name, JSON.stringify(sessionTab));
            this.sessionStorage.removeItem('sessiontab');
            this.handleTabChanges();
            return { ...sessionTab };
        }
        return null;
    }

    cleanupTab(tabName?: string) {
        if (tabName) {
            this.removeTab(tabName);
        } else {
            const sessionTabName = this.sessionStorage.getItem('sessiontab');
            if (sessionTabName) {
                this.removeTab(sessionTabName);
            }
        }

        this.sessionStorage.removeItem('sessiontab');

        const tabHelperKeys = this.localStorage.getAllKeys().filter((key) => key.startsWith('tab:'));
        for (const key of tabHelperKeys) {
            this.localStorage.removeItem(key);
        }
    }

    cleanupAllTabs() {
        this.sessionStorage.removeItem('sessiontab');

        let tabNames = this.localStorage.getAllTabKeys();
        for (const tabName of tabNames) {
            this.removeTab(tabName);
        }

        const tabHelperKeys = this.localStorage.getAllKeys().filter((key) => key.startsWith('tab:'));
        for (const key of tabHelperKeys) {
            this.localStorage.removeItem(key);
        }
    }

    public isItemInMap(name: string): boolean {
        return this.tabObjRef.has(name);
    }

    public setTabObjRef(name: string, tabRef: any) {
        if (!this.isItemInMap(name)) {
            this.tabObjRef.set(name, tabRef);
        }
    }

    public getTabObjRef(name: string): any {
        if (this.isItemInMap(name)) {
            return this.tabObjRef.get(name);
        } else {
            return null;
        }
    }

    getBasePath() {
        let basePath = '';
        if (environment.production) {
            basePath = window.location.protocol + '//' + window.location.hostname + window.location.pathname;
        } else {
            basePath =
                window.location.protocol +
                '//' +
                window.location.hostname +
                ':' +
                window.location.port +
                window.location.pathname;
        }

        if (!basePath.endsWith('/')) {
            basePath += '/';
        }

        return basePath;
    }

    removeStaleTabs() {
        let tabEntries = this.getAllTabs();
        for (const tab of tabEntries) {
            if (this.isTabStale(tab)) {
                console.log(`TabService removing stale tab for: ${tab.Tab_Name}`);
                this.localStorage.removeItem(tab.Tab_Name);
            }
        }
    }

    getTimestamp() {
        let timestampInSeconds = Math.floor(Date.now() / 1000);
        return timestampInSeconds.toString();
    }

    isTabStale(tab: TabData) {
        let nowTS = +this.getTimestamp();
        let tabTS = +tab.Liveness_Timestamp * 1;
        if (nowTS - tabTS >= this.inactivityInterval) {
            return true;
        }
        return false;
    }

    getWindowOptions() {
        return `location=no,toolbar=no,menubar=yes,status=yes,scrollbars=yes,
  resizable=yes,top=250,left=250,width=1100,height=700`;
    }

    generateUUID() {
        var d = new Date().getTime();
        var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = (d + Math.random() * 16) % 16 | 0;
            d = Math.floor(d / 16);
            return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
        return uuid;
    }
}
